import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { overviewRepository } from '@web/data/repositories';
import { queryKeys } from '@web/core/query';
import { t } from '@web/i18n';
import { type CardId, type OverviewRow, type SquadId } from '@voll-intelligence/domain';
import { AppIcons, useOrgContext } from '@web/ui/core';
import { Avatar, AvatarFallback, Button } from '@web/ui/core/atoms';
import { BucketSection, Scaffold } from '@web/ui/core/organisms';
import { fireAndForget, twcn } from '@web/ui/core/utils';

import { OverviewTopBar } from '../components/overview_top_bar';
import { TriagePill } from '../components/triage_pill';

type SectionTone = 'gray' | 'amber' | 'red' | 'ai';

interface BucketSpec {
  id: 'ongoing' | 'vip' | 'queue';
  tone: SectionTone;
  icon: 'headset' | 'crown' | 'users';
  titleKey: Parameters<typeof t>[0];
  noteKey: Parameters<typeof t>[0];
  ctaKey: Parameters<typeof t>[0];
}

const BUCKETS: readonly BucketSpec[] = [
  {
    id: 'ongoing',
    tone: 'ai',
    icon: 'headset',
    titleKey: 'overview.bucket.ongoing.title',
    noteKey: 'overview.bucket.ongoing.note',
    ctaKey: 'overview.bucket.ongoing.cta',
  },
  {
    id: 'vip',
    tone: 'amber',
    icon: 'crown',
    titleKey: 'overview.bucket.vip.title',
    noteKey: 'overview.bucket.vip.note',
    ctaKey: 'overview.bucket.vip.cta',
  },
  {
    id: 'queue',
    tone: 'gray',
    icon: 'users',
    titleKey: 'overview.bucket.queue.title',
    noteKey: 'overview.bucket.queue.note',
    ctaKey: 'overview.bucket.queue.cta',
  },
];

export function OverviewPage(): React.JSX.Element {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const overviewQuery = useQuery({
    queryKey: queryKeys.overview.snapshot(orgId),
    queryFn: () => overviewRepository.loadOverview(orgId),
  });

  const goTriage = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/triage', params: { orgSlug } }));
  };
  // A squad-less row is a card claimed straight from the triage inbox — it
  // has no squad board yet, so it opens in the triage detail instead.
  const openCard = (squadId: SquadId | null, cardId: CardId): void => {
    if (squadId === null) {
      fireAndForget(
        navigate({ to: '/orgs/$orgSlug/triage/card/$cardId', params: { orgSlug, cardId } }),
      );
      return;
    }
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab',
        params: { orgSlug, squadId, cardId, tab: 'conversation' },
      }),
    );
  };
  const openSquad = (squadId: SquadId | null): void => {
    if (squadId === null) {
      goTriage();
      return;
    }
    fireAndForget(navigate({ to: '/orgs/$orgSlug/squad/$squadId', params: { orgSlug, squadId } }));
  };

  // `null` = snapshot still loading: every slot below renders a placeholder
  // instead of a fake "calm" zero state.
  const overview = overviewQuery.data ?? null;
  const pending = overview === null && !overviewQuery.isError;
  const aiCount = overview?.aiCount ?? null;
  const humanCount = overview?.humanCount ?? null;
  // Headline counts distinct customers (people) you're personally attending —
  // deduped across cards, and excluding the unassigned human queue (that waits
  // for "um humano", not for you).
  const attendingCustomerCount =
    overview === null ? null : new Set(overview.ongoing.map((row) => row.customer.id)).size;
  const triageTotal = overview?.triage.total ?? null;

  return (
    <Scaffold
      appBar={
        <OverviewTopBar aiCount={aiCount} humanCount={humanCount} failed={overviewQuery.isError} />
      }
      body={
        overviewQuery.isError ? (
          <>
            <OverviewEyebrow live={false} />
            <p role="alert" className="text-meta text-tone-red-fg">
              {t('overview.error')}
            </p>
          </>
        ) : (
          <>
            <header className="flex flex-col gap-2">
              <OverviewEyebrow live />
              {pending ? (
                <span role="status" className="sr-only">
                  {t('overview.loading')}
                </span>
              ) : null}
              <div className="flex flex-row justify-between gap-2">
                <h1
                  aria-busy={pending || undefined}
                  className="text-display-lg m-0 leading-display tracking-display tabular-nums"
                >
                  <HeroTitle attendingCustomerCount={attendingCustomerCount} />
                </h1>
                <TriagePill total={triageTotal} onClick={goTriage} />
              </div>
            </header>

            <div className="flex flex-col gap-4 py-3">
              {BUCKETS.map((bucket) => (
                <OverviewBucket
                  key={bucket.id}
                  spec={bucket}
                  rows={overview === null ? null : overview[bucket.id]}
                  onOpenCard={openCard}
                  onOpenSquad={openSquad}
                />
              ))}
            </div>
          </>
        )
      }
    />
  );
}

function OverviewEyebrow({ live }: Readonly<{ live: boolean }>): React.JSX.Element {
  return (
    <span className="text-eyebrow inline-flex items-center gap-2">
      <span>{t('overview.eyebrow')}</span>
      {live ? (
        <>
          <span aria-hidden className="text-faint-foreground">
            ·
          </span>
          <span>{t('overview.eyebrowLive')}</span>
        </>
      ) : null}
    </span>
  );
}

function HeroTitle({
  attendingCustomerCount,
}: Readonly<{
  /** `null` while the snapshot is loading. */
  attendingCustomerCount: number | null;
}>): React.JSX.Element {
  if (attendingCustomerCount === null) {
    return (
      <span
        aria-hidden
        className="inline-block h-10 w-2/3 animate-pulse rounded-md bg-muted align-middle"
      />
    );
  }
  if (attendingCustomerCount === 0) {
    return (
      <>
        {t('overview.title.calm.prefix')}{' '}
        <em className="not-italic text-primary">{t('overview.title.calm.emphasis')}</em>.
      </>
    );
  }
  return (
    <>
      <em className="not-italic text-primary">{attendingCustomerCount}</em>{' '}
      {t('overview.title.todo', { count: attendingCustomerCount })}
    </>
  );
}

function OverviewBucket({
  spec,
  rows,
  onOpenCard,
  onOpenSquad,
}: Readonly<{
  spec: BucketSpec;
  /** `null` while the snapshot is loading. */
  rows: readonly OverviewRow[] | null;
  onOpenCard: (squadId: SquadId | null, cardId: CardId) => void;
  onOpenSquad: (squadId: SquadId | null) => void;
}>): React.JSX.Element {
  const isOngoing = spec.id === 'ongoing';
  return (
    <BucketSection
      tone={spec.tone}
      icon={spec.icon}
      title={t(spec.titleKey)}
      note={t(spec.noteKey)}
      count={rows === null ? null : rows.length}
      testId={`overview-bucket-${spec.id}`}
    >
      {rows === null && (
        <ul className="flex flex-col" data-testid={`overview-bucket-${spec.id}-loading`}>
          {[0, 1].map((i) => (
            <li key={i} className="h-20 border-t border-border-soft px-4 py-3.5 first:border-t-0">
              <span className="block h-full animate-pulse rounded-md bg-muted" />
            </li>
          ))}
        </ul>
      )}
      {rows?.length === 0 && (
        <div className="px-5 py-6 text-center text-meta text-faint-foreground">
          {t('overview.bucket.empty')}
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <BucketRow
              key={row.cardId}
              row={row}
              ctaKey={spec.ctaKey}
              isOngoing={isOngoing}
              onOpenCard={onOpenCard}
              onOpenSquad={onOpenSquad}
            />
          ))}
        </ul>
      )}
    </BucketSection>
  );
}

function BucketRow({
  row,
  ctaKey,
  isOngoing,
  onOpenCard,
  onOpenSquad,
}: Readonly<{
  row: OverviewRow;
  ctaKey: Parameters<typeof t>[0];
  isOngoing: boolean;
  onOpenCard: (squadId: SquadId | null, cardId: CardId) => void;
  onOpenSquad: (squadId: SquadId | null) => void;
}>): React.JSX.Element {
  const cardIdShort = `#${row.cardId.slice(-4)}`;
  const squadLabel = row.squadName ?? t('overview.row.triage');
  const squadAria =
    row.squadName === null
      ? t('overview.row.openTriage')
      : t('overview.row.openSquad', { squad: row.squadName });
  return (
    <li
      data-testid="overview-row"
      data-card-id={row.cardId}
      className={twcn(
        'grid grid-cols-[1fr_auto] items-stretch border-t border-border-soft first:border-t-0 transition-colors duration-base ease-standard hover:bg-muted',
        isOngoing && 'bg-ai-tint/24 hover:bg-ai-tint/40',
      )}
    >
      <div className="flex items-start gap-3 pl-4 pr-3 py-3.5 min-w-0">
        <span className="mt-0.5 flex-shrink-0">
          <Avatar size="md" tone={row.customer.vip ? 'vip' : 'neutral'}>
            <AvatarFallback displayName={row.customer.name} />
          </Avatar>
        </span>
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="max-w-row-name flex-shrink-0 truncate text-card-title font-medium tracking-display-soft text-foreground">
              {row.customer.name}
            </span>
            <span className="truncate text-xs text-muted-foreground min-w-0">
              {row.company.name}
            </span>
          </div>
          <p className="m-0 line-clamp-1 text-meta leading-snug text-foreground">{row.preview}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                onOpenSquad(row.squadId);
              }}
              aria-label={squadAria}
              className="inline-flex items-center gap-1 rounded-sm bg-transparent px-1.5 py-0.5 text-card-id text-muted-foreground transition-colors duration-base ease-standard hover:bg-border-soft hover:text-foreground"
            >
              <AppIcons name="users" size={12} className="text-faint-foreground" />
              <span>{squadLabel}</span>
            </button>
            <span className="font-mono text-tiny text-faint-foreground">{cardIdShort}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 pl-1 pr-4 py-3.5 flex-shrink-0">
        {isOngoing ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-card-id uppercase tracking-eyebrow text-muted-foreground">
            <span aria-hidden className="h-1.75 w-1.75 rounded-pill bg-tone-amber-fg" />
            <span>{t('overview.row.ongoingStatus')}</span>
          </span>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            onOpenCard(row.squadId, row.cardId);
          }}
        >
          <span>{t(ctaKey)}</span>
          <AppIcons name="arrow-right" size={12} />
        </Button>
      </div>
    </li>
  );
}
